import type { ProjectStatus } from '../api/types.js';

/** The stages a project moves through, in order. */
export const PROCESS_STEPS = [
  'Answer questionnaire',
  'Generate draft specification',
  'Review by technologist',
  'Specification document created',
  'Approved by you',
  'In progress',
  'First iteration produced',
  'Ongoing refinement',
] as const;

/**
 * Maps a project status to the step currently in progress. Completed statuses
 * advance past their milestone so the finished step reads as done (green ✓):
 * once the founder has approved (`spec_approved`) the "Approved by you" step is
 * complete and the project is "In progress". Every non-archived status must map
 * to a step — a missing one would fall through and wrongly show the first step.
 */
export function stepForStatus(status: ProjectStatus): number {
  switch (status) {
    case 'questionnaire':
      return 0; // answering the questionnaire
    case 'generating_spec':
      return 1; // generating the draft
    case 'spec_review':
      return 2; // technologist reviewing
    case 'spec_shared':
      return 4; // document created + shared → awaiting your approval
    case 'spec_approved':
      return 5; // approved → in progress
    case 'generating_stories':
      return 5; // splitting into stories → in progress
    case 'building':
      return 5; // in progress
    case 'ongoing_refinement':
      return 7; // shipped and being refined
    default:
      return 0; // archived — the banner in the shell handles this
  }
}

export function Stepper({ current }: { current: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-3">
      {PROCESS_STEPS.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'todo';
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                state === 'done'
                  ? 'bg-primary text-white'
                  : state === 'current'
                    ? 'bg-secondary text-white'
                    : 'bg-neutral text-secondary/50'
              }`}
            >
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span
              className={`text-sm ${
                state === 'todo' ? 'opacity-50' : state === 'current' ? 'font-medium' : ''
              }`}
            >
              {label}
            </span>
            {i < PROCESS_STEPS.length - 1 ? (
              <span className="mx-1 hidden h-px w-6 bg-secondary/20 sm:block" aria-hidden="true" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
